// src/pages/dashboard/StudentDashboard.jsx
// Copy this complete code into your StudentDashboard.jsx file.
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  BookOpenIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ChartBarIcon,
  AcademicCapIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import apiClient from "../../api/client";
import toast from "react-hot-toast";
import { FireLoader } from "../../components/Loader";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ========== Helper functions to map activity type to UI ==========
const getActivityIcon = (type) => {
  switch (type?.toLowerCase()) {
    case "attendance":
      return CheckCircleIcon;
    case "grade":
      return ChartBarIcon;
    case "assignment":
      return BookOpenIcon;
    default:
      return ClockIcon;
  }
};

const getActivityIconColor = (type) => {
  switch (type?.toLowerCase()) {
    case "attendance":
      return "text-green-500";
    case "grade":
      return "text-blue-500";
    case "assignment":
      return "text-purple-500";
    default:
      return "text-gray-500";
  }
};

const getActivityBgColor = (type) => {
  switch (type?.toLowerCase()) {
    case "attendance":
      return "bg-green-50";
    case "grade":
      return "bg-blue-50";
    case "assignment":
      return "bg-purple-50";
    default:
      return "bg-gray-50";
  }
};

const TERM_LABELS = {
  term_1: "Term 1",
  term_2: "Term 2",
  term_3: "Term 3",
};

const ASSESSMENT_FIELDS = [
  { key: "test1", label: "Test 1" },
  { key: "test2", label: "Test 2" },
  { key: "exam", label: "Exam" },
];

const getActivityTime = (value) => {
  if (!value) return 0;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

const STUDENT_TOUR_KEY = "pawaschool-student-dashboard-tour-v1";

const studentTourSteps = [
  {
    target: "student-welcome",
    title: "Welcome to your dashboard",
    description:
      "This is your personal learning space. Everything here is connected to your own class and school records.",
  },
  {
    target: "student-shortcuts",
    title: "Open your learning tools",
    description:
      "Use these shortcuts to quickly view your grades, attendance, class timetable and assigned subjects.",
  },
  {
    target: "student-summary",
    title: "Check your academic summary",
    description:
      "These cards show your total subjects, average grade, attendance rate and upcoming classes at a glance.",
  },
  {
    target: "student-performance",
    title: "Follow your performance",
    description:
      "This chart helps you understand how your average grade changes across each term and the full academic year.",
  },
  {
    target: "student-subjects",
    title: "Review your subjects",
    description:
      "See the subjects assigned to you and your latest available score for each one.",
  },
  {
    target: "student-activity",
    title: "Keep up with recent activity",
    description:
      "New grades, subject assignments and attendance updates appear here so you can stay informed.",
  },
];

function StudentDashboardTour({ step, onNext, onBack, onClose }) {
  const [position, setPosition] = useState(null);
  const currentStep = studentTourSteps[step];

  useEffect(() => {
    const target = document.querySelector(
      `[data-tour="${currentStep.target}"]`,
    );
    if (!target) {
      onNext();
      return undefined;
    }

    let animationFrame;
    let settleTimer;
    const previousStyles = {
      position: target.style.position,
      zIndex: target.style.zIndex,
      isolation: target.style.isolation,
      boxShadow: target.style.boxShadow,
      transition: target.style.transition,
      borderRadius: target.style.borderRadius,
      pointerEvents: target.style.pointerEvents,
    };

    if (window.getComputedStyle(target).position === "static") {
      target.style.position = "relative";
    }
    target.style.zIndex = "100";
    target.style.isolation = "isolate";
    target.style.borderRadius = "16px";
    target.style.pointerEvents = "none";
    target.style.transition =
      "box-shadow 420ms cubic-bezier(0.22,1,0.36,1), transform 420ms cubic-bezier(0.22,1,0.36,1)";
    target.style.boxShadow =
      "0 0 0 4px rgba(255,255,255,0.98), 0 18px 48px rgba(15,23,42,0.30)";
    target.classList.add("student-tour-target");

    const updatePosition = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const rect = target.getBoundingClientRect();
        const isMobile = window.innerWidth < 768;
        const margin = isMobile ? 12 : 20;
        const gap = 20;
        const tooltipWidth = isMobile
          ? window.innerWidth - margin * 2
          : Math.min(360, window.innerWidth - margin * 2);
        const tooltipHeight = 270;
        const spaces = {
          bottom: window.innerHeight - rect.bottom,
          top: rect.top,
          right: window.innerWidth - rect.right,
          left: rect.left,
        };

        let top;
        let left;

        if (isMobile) {
          top = undefined;
          left = margin;
        } else if (spaces.right >= tooltipWidth + gap) {
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.right + gap;
        } else if (spaces.left >= tooltipWidth + gap) {
          top = rect.top + rect.height / 2 - tooltipHeight / 2;
          left = rect.left - tooltipWidth - gap;
        } else if (spaces.bottom >= tooltipHeight + gap) {
          top = rect.bottom + gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
        } else {
          top = rect.top - tooltipHeight - gap;
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
        }

        if (!isMobile) {
          top = Math.max(
            margin,
            Math.min(top, window.innerHeight - tooltipHeight - margin),
          );
          left = Math.max(
            margin,
            Math.min(left, window.innerWidth - tooltipWidth - margin),
          );
        }

        setPosition({
          top,
          bottom: isMobile ? margin : undefined,
          left,
          width: tooltipWidth,
          isMobile,
        });
      });
    };

    setPosition(null);
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    updatePosition();
    settleTimer = window.setTimeout(updatePosition, 500);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, { passive: true });

    return () => {
      window.clearTimeout(settleTimer);
      window.cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
      target.classList.remove("student-tour-target");
      Object.assign(target.style, previousStyles);
    };
  }, [currentStep.target, onNext]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") onNext();
      if (event.key === "ArrowLeft" && step > 0) onBack();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onBack, onClose, onNext, step]);

  if (!position) return null;

  return (
    <>
      <div className="fixed inset-0 z-[90] bg-slate-950/70 backdrop-blur-[1px] animate-[student-tour-fade_300ms_ease-out]" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-tour-title"
        className={`fixed z-[110] overflow-hidden border border-gray-100 bg-white shadow-xl animate-[student-tour-enter_420ms_cubic-bezier(0.22,1,0.36,1)] ${
          position.isMobile ? "rounded-2xl" : "rounded-xl"
        }`}
        style={{
          top: position.top,
          bottom: position.bottom,
          left: position.left,
          width: position.width,
        }}
      >
        <div className="h-1.5 bg-gray-100">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 transition-all duration-500"
            style={{
              width: `${((step + 1) / studentTourSteps.length) * 100}%`,
            }}
          />
        </div>

        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <SparklesIcon className="h-5 w-5" />
            </span>
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 -mt-2 rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close onboarding"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <p className="mt-4 text-xs font-bold uppercase tracking-wider text-primary-500">
            Step {step + 1} of {studentTourSteps.length}
          </p>
          <h2
            id="student-tour-title"
            className="mt-1 text-xl font-bold text-gray-900"
          >
            {currentStep.title}
          </h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            {currentStep.description}
          </p>

          <div className="mt-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={step === 0 ? onClose : onBack}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-gray-500 transition hover:text-gray-800"
            >
              {step === 0 ? (
                "Skip tour"
              ) : (
                <>
                  <ChevronLeftIcon className="h-4 w-4" /> Back
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onNext}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition duration-200 hover:-translate-y-0.5 hover:bg-primary-700 active:translate-y-0"
            >
              {step === studentTourSteps.length - 1 ? "Finish" : "Next"}
              {step < studentTourSteps.length - 1 && (
                <ChevronRightIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [stats, setStats] = useState({
    totalSubjects: 0,
    averageGrade: 0,
    attendanceRate: 0,
    attendanceSessions: 0,
  });
  const [activities, setActivities] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [tourStep, setTourStep] = useState(null);
  const [dashboardError, setDashboardError] = useState("");

  const tourUserId = user?.uuid || user?.id || user?.email;
  const tourSchoolId = user?.school_id || user?.schoolId;
  const tourIdentity = [tourUserId, tourSchoolId].filter(Boolean).join(":");
  const tourStorageKey = tourIdentity
    ? `${STUDENT_TOUR_KEY}:${tourIdentity}`
    : null;

  const completeTour = useCallback(() => {
    if (tourStorageKey) {
      try {
        localStorage.setItem(tourStorageKey, "completed");
      } catch {
        // The visible tour still closes if browser storage is unavailable.
      }
    }
    setTourStep(null);
  }, [tourStorageKey]);

  const nextTourStep = useCallback(() => {
    setTourStep((current) => {
      if (current === null || current >= studentTourSteps.length - 1) {
        if (tourStorageKey) {
          try {
            localStorage.setItem(tourStorageKey, "completed");
          } catch {
            // Finishing the current tour does not depend on storage support.
          }
        }
        return null;
      }
      return current + 1;
    });
  }, [tourStorageKey]);

  const previousTourStep = useCallback(() => {
    setTourStep((current) => Math.max(0, (current ?? 0) - 1));
  }, []);

  useEffect(() => {
    fetchStudentData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (loading || !student || !tourStorageKey) return undefined;

    let completed = false;
    try {
      completed = localStorage.getItem(tourStorageKey) === "completed";
    } catch {
      completed = false;
    }

    if (completed) return undefined;

    const timer = window.setTimeout(() => setTourStep(0), 800);
    return () => window.clearTimeout(timer);
  }, [loading, student, tourStorageKey]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      setDashboardError("");
      setStudent(null);
      setClassInfo(null);
      setSubjects([]);
      setGrades([]);
      setActivities([]);
      setPerformanceData([]);
      setStats({
        totalSubjects: 0,
        averageGrade: 0,
        attendanceRate: 0,
        attendanceSessions: 0,
      });

      if (!user?.id) {
        throw new Error("Your signed-in user information is unavailable.");
      }

      const studentRes = await apiClient.get(`/students/by-user/${user.id}`);
      if (!studentRes.data?.success || !studentRes.data?.data) {
        throw new Error("No student profile was found for your account.");
      }
      const studentData = studentRes.data.data;

      let studentWithClass = studentData;
      if (!studentData.class) {
        try {
          const detailRes = await apiClient.get(`/students/${studentData.id}`);
          if (detailRes.data.success) {
            studentWithClass = detailRes.data.data;
          } else {
            console.warn("Could not fetch detailed student info");
          }
        } catch (detailErr) {
          console.warn("Error fetching student details:", detailErr);
        }
      }

      setStudent(studentWithClass);
      setClassInfo({
        id: studentWithClass.current_class_id,
        name: studentWithClass.class?.name || "Class not assigned",
      });

      let subjectList = [];
      try {
        const subjectsRes = await apiClient.get(
          `/students/${studentData.id}/subjects`,
        );
        if (subjectsRes.data?.success) {
          subjectList = Array.isArray(subjectsRes.data.data)
            ? subjectsRes.data.data
            : [];
        }
      } catch (subjectError) {
        console.warn("Could not fetch student subjects:", subjectError);
      }
      setSubjects(subjectList);

      let gradeData = [];
      try {
        const gradesRes = await apiClient.get(
          `/students/${studentData.id}/grades`,
        );
        if (gradesRes.data?.success && Array.isArray(gradesRes.data.data)) {
          gradeData = gradesRes.data.data;
        }
      } catch (gradeErr) {
        console.warn("Could not fetch student grades:", gradeErr);
      }
      setGrades(gradeData);

      const termKeys = ["term_1", "term_2", "term_3"];
      const termTotals = { term_1: 0, term_2: 0, term_3: 0 };
      let yearlyTotal = 0;
      const subjectCount = gradeData.length;

      // Keep this calculation identical to StudentGradeSheet. Each subject is
      // included in the denominator, and a missing term/year mean contributes 0.
      gradeData.forEach((item) => {
        termKeys.forEach((term) => {
          const mean = Number(item?.terms?.[term]?.mean ?? 0);
          termTotals[term] += Number.isFinite(mean) ? mean : 0;
        });

        const yearlyMean = Number(item?.yearly?.mean ?? 0);
        yearlyTotal += Number.isFinite(yearlyMean) ? yearlyMean : 0;
      });

      const divisor = subjectCount || 1;
      const termAverageValues = {
        term_1: Math.round(termTotals.term_1 / divisor),
        term_2: Math.round(termTotals.term_2 / divisor),
        term_3: Math.round(termTotals.term_3 / divisor),
      };
      const yearlyAverage = Math.round(yearlyTotal / divisor);

      const chartData = termKeys.map((term) => ({
        name: term.replace("term_", "Term "),
        Average: termAverageValues[term],
      }));
      chartData.push({ name: "Yearly", Average: yearlyAverage });
      setPerformanceData(chartData);

      let attendanceRecords = [];
      try {
        const attendanceRes = await apiClient.get(
          `/students/${studentData.id}/attendance`,
        );
        if (
          attendanceRes.data?.success &&
          Array.isArray(attendanceRes.data.data)
        ) {
          attendanceRecords = attendanceRes.data.data;
        }
      } catch (attendanceError) {
        console.warn("Could not fetch student attendance:", attendanceError);
      }

      const attendedSessions = attendanceRecords.filter((record) =>
        ["present", "late"].includes(String(record.status || "").toLowerCase()),
      ).length;
      const attendanceRate = attendanceRecords.length
        ? Math.round((attendedSessions / attendanceRecords.length) * 100)
        : 0;

      setStats({
        totalSubjects: subjectList.length,
        averageGrade: yearlyAverage,
        attendanceRate,
        attendanceSessions: attendanceRecords.length,
      });

      // Build one real activity feed, sort the complete feed, then keep only
      // the five newest records for the dashboard preview.
      const gradeActivities = gradeData.flatMap((grade, gradeIndex) => {
        const subjectName =
          grade.subject || grade.subject_name || grade.name || "Subject";
        const subjectId = grade.subject_id || grade.subjectId || gradeIndex;

        return Object.entries(TERM_LABELS).flatMap(([termKey, termLabel]) => {
          const term = grade.terms?.[termKey];
          if (!term) return [];

          return ASSESSMENT_FIELDS.flatMap(({ key, label }) => {
            const rawScore = term[key];
            const score = Number(rawScore);

            // Zero and blank values are unposted placeholders in the grade
            // response, so they must not appear as student activity.
            if (
              rawScore === null ||
              rawScore === undefined ||
              rawScore === "" ||
              !Number.isFinite(score) ||
              score <= 0
            ) {
              return [];
            }

            return [
              {
                id: `grade-${subjectId}-${termKey}-${key}`,
                user: subjectName,
                action: `${label} for ${termLabel}: ${score}%`,
                time:
                  term.updated_at ||
                  term.updatedAt ||
                  term.created_at ||
                  term.createdAt ||
                  grade.updated_at ||
                  grade.updatedAt ||
                  grade.created_at ||
                  grade.createdAt ||
                  null,
                type: "grade",
                icon: getActivityIcon("grade"),
                iconColor: getActivityIconColor("grade"),
                bgColor: getActivityBgColor("grade"),
              },
            ];
          });
        });
      });

      const attendanceActivities = attendanceRecords.map((record) => {
        const status = String(record.status || "recorded").toLowerCase();
        const session = record.session
          ? ` for the ${record.session} session`
          : "";
        return {
          id: `attendance-${record.id}`,
          user: "Attendance",
          action: `marked ${status}${session}`,
          time:
            record.date ||
            record.updated_at ||
            record.updatedAt ||
            record.created_at ||
            record.createdAt ||
            null,
          type: "attendance",
          icon: getActivityIcon("attendance"),
          iconColor: getActivityIconColor("attendance"),
          bgColor: getActivityBgColor("attendance"),
        };
      });

      const builtActivities = [
        ...gradeActivities,
        ...attendanceActivities,
      ].sort((a, b) => {
        const timeDifference =
          getActivityTime(b.time) - getActivityTime(a.time);
        if (timeDifference !== 0) return timeDifference;
        return String(b.id).localeCompare(String(a.id));
      });

      setActivities(builtActivities.slice(0, 5));
    } catch (error) {
      console.error("Error fetching student data:", error);
      toast.error("Failed to load student dashboard");
      setDashboardError(
        error.response?.data?.message ||
          error.message ||
          "We could not load your student information.",
      );
    } finally {
      setLoading(false);
    }
  };

  const timeAgo = (date) => {
    if (!date) return "Just now";
    const now = new Date();
    const past = new Date(date);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return past.toLocaleDateString();
  };

  if (loading) return <FireLoader fullScreen size="lg" text="Loading..." />;

  if (!student) {
    return (
      <div className="min-h-[65vh] flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg rounded-2xl border border-gray-100 bg-white p-7 sm:p-10 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-50">
            <AcademicCapIcon className="h-8 w-8 text-primary-600" />
          </div>
          <h2 className="mt-5 text-xl font-bold text-gray-900">
            Student information unavailable
          </h2>
          <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-gray-500">
            {dashboardError ||
              "No student profile has been linked to this account yet."}
          </p>
          <button
            type="button"
            onClick={fetchStudentData}
            className="mt-6 inline-flex items-center justify-center rounded-xl bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Use only the first name in the welcome header
  const firstName = student.first_name || "Student";

  return (
    <>
      {/* Waving hand animation */}
      <style>{`
        @keyframes wave {
          0% { transform: rotate(0deg); }
          25% { transform: rotate(15deg); }
          50% { transform: rotate(0deg); }
          75% { transform: rotate(-15deg); }
          100% { transform: rotate(0deg); }
        }
        .wave-emoji {
          display: inline-block;
          animation: wave 1.5s ease-in-out infinite;
          transform-origin: 70% 70%;
        }
        @media (max-width: 480px) {
          .responsive-padding {
            padding-left: 0.75rem;
            padding-right: 0.75rem;
          }
        }
        .card-hover {
          transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .card-hover:hover {
          transform: translateY(-2px);
          border-color: #a5b4fc;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.04);
        }
        .stat-card-hover {
          transition: transform 0.2s ease, border-color 0.2s ease;
        }
        .stat-card-hover:hover {
          transform: translateY(-2px);
          border-color: #a5b4fc;
        }
        .activity-row {
          transition: background 0.15s ease;
          border-radius: 0.75rem;
        }
        .activity-row:hover {
          background: #f8fafc;
        }
        @keyframes student-tour-enter {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes student-tour-fade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes student-tour-highlight {
          0%, 100% {
            box-shadow: 0 0 0 4px rgba(255,255,255,0.98), 0 18px 48px rgba(15,23,42,0.26);
          }
          50% {
            box-shadow: 0 0 0 7px rgba(59,130,246,0.38), 0 22px 58px rgba(15,23,42,0.36);
          }
        }
        .student-tour-target {
          animation: student-tour-highlight 1.8s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 sm:space-y-8 responsive-padding">
        {/* ===== HEADER (now only displays first name) ===== */}
        <div
          data-tour="student-welcome"
          className="relative overflow-hidden rounded-xl bg-primary-600 border border-primary-500/20 px-5 sm:px-8 py-5 sm:py-7 text-white"
        >
          <div className="absolute right-0 top-0 opacity-10 pointer-events-none">
            <AcademicCapIcon className="w-32 sm:w-48 h-32 sm:h-48" />
          </div>
          <div className="relative z-10">
            <h1 className="text-xl sm:text-3xl font-bold leading-tight">
              Welcome back, {firstName}
              <span className="wave-emoji ml-2" style={{ fontSize: "inherit" }}>
                👋
              </span>
            </h1>
            <p className="text-primary-100 text-sm sm:text-base mt-1 max-w-2xl">
              Stay focused, keep learning, and make this school year amazing!
            </p>
          </div>
        </div>

        {/* ===== QUICK ACTION TILES ===== */}
        <div
          data-tour="student-shortcuts"
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
        >
          <div
            onClick={() => navigate("/app/grades")}
            className="card-hover bg-white rounded-xl border border-gray-200 p-3 sm:p-4 text-center cursor-pointer hover:border-primary-300"
          >
            <ChartBarIcon className="w-7 h-7 sm:w-8 sm:h-8 text-blue-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              View Grades
            </span>
          </div>
          <div
            onClick={() => navigate("/app/attendance")}
            className="card-hover bg-white rounded-xl border border-gray-200 p-3 sm:p-4 text-center cursor-pointer hover:border-primary-300"
          >
            <CalendarDaysIcon className="w-7 h-7 sm:w-8 sm:h-8 text-purple-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              Attendance
            </span>
          </div>
          <div
            onClick={() => navigate("/app/timetable")}
            className="card-hover bg-white rounded-xl border border-gray-200 p-3 sm:p-4 text-center cursor-pointer hover:border-primary-300"
          >
            <ClockIcon className="w-7 h-7 sm:w-8 sm:h-8 text-orange-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              Timetable
            </span>
          </div>
          <div
            onClick={() => navigate("/app/subjects")}
            className="card-hover bg-white rounded-xl border border-gray-200 p-3 sm:p-4 text-center cursor-pointer hover:border-primary-300"
          >
            <BookOpenIcon className="w-7 h-7 sm:w-8 sm:h-8 text-teal-600 mx-auto mb-1 sm:mb-2" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              Subjects
            </span>
          </div>
        </div>

        {/* ===== STATS CARDS ===== */}
        <div
          data-tour="student-summary"
          className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4"
        >
          <div className="stat-card-hover bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <BookOpenIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                Subjects
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">
                {stats.totalSubjects}
              </p>
            </div>
          </div>
          <div className="stat-card-hover bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                Average Grade
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">
                {`${stats.averageGrade || 0}%`}
              </p>
            </div>
          </div>
          <div className="stat-card-hover bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
              <CheckCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                Attendance
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">
                {`${stats.attendanceRate || 0}%`}
              </p>
            </div>
          </div>
          <div className="stat-card-hover bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
              <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                Attendance Sessions
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">
                {stats.attendanceSessions || 0}
              </p>
            </div>
          </div>
        </div>

        {/* ===== PERFORMANCE CHART ===== */}
        <div
          data-tour="student-performance"
          className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800">
              Academic Performance
            </h3>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-blue-500"></span>{" "}
                Average Grade
              </span>
            </div>
          </div>
          {performanceData.length > 0 &&
          performanceData.some((d) => d.Average > 0) ? (
            <div className="w-full h-52 sm:h-64 md:h-72 lg:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={performanceData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: "#6b7280", fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      borderRadius: "0.5rem",
                      border: "1px solid #e5e7eb",
                      fontSize: "12px",
                    }}
                    formatter={(value) => [`${value}%`, "Average"]}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar dataKey="Average" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No grade data available.
            </div>
          )}
          <div className="mt-2 text-[10px] sm:text-xs text-gray-400 text-center border-t border-gray-100 pt-2">
            Overall average per term and yearly aggregate
          </div>
        </div>

        {/* ===== SUBJECTS + ACTIVITY TWO-COLUMN ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Subjects */}
          <div className="lg:col-span-1">
            <div
              data-tour="student-subjects"
              className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 h-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  My Subjects
                </h3>
                <button
                  onClick={() => navigate("/app/subjects")}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View All
                </button>
              </div>
              <div className="space-y-2 sm:space-y-2.5">
                {subjects.length > 0 ? (
                  subjects.slice(0, 5).map((subj) => {
                    let avg = null;
                    if (grades.length > 0 && grades[0]?.terms) {
                      const subjectGrade = grades.find(
                        (g) => g.subject === subj.name,
                      );
                      if (subjectGrade) {
                        const termOrder = ["term_3", "term_2", "term_1"];
                        for (const term of termOrder) {
                          if (
                            subjectGrade.terms[term] &&
                            typeof subjectGrade.terms[term].mean === "number"
                          ) {
                            avg = subjectGrade.terms[term].mean;
                            break;
                          }
                        }
                      }
                    } else {
                      const subjectGrades = grades.filter(
                        (g) => g.subject_id === subj.id,
                      );
                      const termOrder = ["term_3", "term_2", "term_1"];
                      let latest = null;
                      for (const term of termOrder) {
                        const g = subjectGrades.find((g) => g.term === term);
                        if (g) {
                          latest = g;
                          break;
                        }
                      }
                      if (latest) {
                        avg = Math.round((latest.test_1 + latest.test_2) / 2);
                      }
                    }
                    return (
                      <div
                        key={subj.id}
                        className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 hover:border-primary-200 transition-all"
                      >
                        <span className="text-xs sm:text-sm font-medium text-gray-700 truncate">
                          {subj.name}
                        </span>
                        <span
                          className={`text-xs sm:text-sm font-semibold ${
                            avg !== null
                              ? avg >= 70
                                ? "text-emerald-600"
                                : "text-amber-600"
                              : "text-gray-400"
                          }`}
                        >
                          {avg !== null ? `${avg}%` : "0%"}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <p className="text-gray-400 text-sm">
                    No subjects assigned yet.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="lg:col-span-2">
            <div
              data-tour="student-activity"
              className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 h-full"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Recent Activity
                </h3>
                <button
                  onClick={() => navigate("/app/notifications")}
                  className="text-xs sm:text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {activities.length > 0 ? (
                  activities.slice(0, 5).map((activity) => (
                    <div
                      key={activity.id}
                      className="activity-row flex items-center gap-3 px-3 py-2.5 rounded-xl border border-transparent hover:border-gray-100 transition-all"
                    >
                      <div
                        className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg ${
                          activity.bgColor || "bg-gray-100"
                        } flex items-center justify-center flex-shrink-0`}
                      >
                        <activity.icon
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${
                            activity.iconColor || "text-gray-500"
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm text-gray-700 break-words">
                          <span className="font-medium">{activity.user}</span>{" "}
                          {activity.action}
                        </p>
                        <p className="text-xs text-gray-400">
                          {timeAgo(activity.time)}
                        </p>
                      </div>
                      <span
                        className={`px-2 py-1 text-[10px] sm:text-xs rounded-full whitespace-nowrap font-medium ${
                          activity.type === "attendance"
                            ? "bg-emerald-50 text-emerald-600"
                            : activity.type === "grade"
                              ? "bg-blue-50 text-blue-600"
                              : "bg-gray-50 text-gray-600"
                        } capitalize`}
                      >
                        {activity.type}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <ClockIcon className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-400 text-sm">No recent activity</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {tourStep !== null && (
        <StudentDashboardTour
          step={tourStep}
          onNext={nextTourStep}
          onBack={previousTourStep}
          onClose={completeTour}
        />
      )}
    </>
  );
}